import { extensionOf, inlineByteSize, typeLabel } from './artifact-file';
import { creatorLabel, creatorOf, nameOf, type BrowsableFile } from './file-browser';

// Sorting the Files table. EVERY column sorts, because a column header that looks like the two beside it and
// does nothing when clicked is worse than no affordance at all — and "which of these is biggest / oldest /
// whose is this" are the questions a file list exists to answer.
//
// One rule runs through all five: **a row with nothing in that column goes last, in both directions.** An
// undated file is unknown, not ancient; an unattributed one is unknown, not "A". Letting the missing values
// fall wherever `Array.sort`'s stability leaves them makes the same list order differently depending on how it
// arrived, which reads as a bug even when the data is fine.

export const FILE_SORT_KEYS = ['name', 'createdBy', 'date', 'type', 'size'] as const;
export type FileSortKey = (typeof FILE_SORT_KEYS)[number];
export type FileSortDirection = 'asc' | 'desc';

export interface FileSort {
  key: FileSortKey;
  direction: FileSortDirection;
}

/** The default: newest first. A file list is usually read as "what happened lately". */
export const DEFAULT_FILE_SORT: FileSort = { key: 'date', direction: 'desc' };

/** Which direction a column starts in when you first click it. Dates and sizes are interesting from the top
 *  (newest, biggest); text is interesting from A. */
export function initialDirection(key: FileSortKey): FileSortDirection {
  return key === 'date' || key === 'size' ? 'desc' : 'asc';
}

/** Clicking a header: the same column flips direction, a new column starts at its own default. */
export function nextSort(current: FileSort, key: FileSortKey): FileSort {
  if (current.key !== key) return { key, direction: initialDirection(key) };
  return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

/** What a row shows in a column, as the value to sort on — `null` meaning "nothing here". A string sorts by
 *  locale, a number numerically; the caller supplies the creator label because it needs translations. */
function valueFor(item: BrowsableFile, key: FileSortKey, creatorTitle: (item: BrowsableFile) => string | null): string | number | null {
  switch (key) {
    case 'name':
      // The DISPLAYED name — its last path segment. Sorting on the stored path would group by folder, which is
      // what the folder rows are for.
      return nameOf(item.name);
    case 'createdBy':
      return creatorTitle(item);
    case 'date': {
      const stamp = item.updatedDate ?? item.createdDate;
      if (!stamp) return null;
      const time = new Date(stamp).getTime();
      return Number.isNaN(time) ? null : time;
    }
    case 'type':
      return typeLabel(extensionOf(item)) ?? null;
    case 'size':
      return inlineByteSize(item);
  }
}

/** `items` sorted by `sort`. Pure, and stable for equal values: ties fall back to the file name so two files
 *  written in the same second don't swap places between renders. */
export function sortFiles<T extends BrowsableFile>(
  items: T[],
  sort: FileSort,
  creatorTitle: (item: BrowsableFile) => string | null,
): T[] {
  const sign = sort.direction === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const left = valueFor(a, sort.key, creatorTitle);
    const right = valueFor(b, sort.key, creatorTitle);
    // Missing values sink, whichever way the column is pointing.
    if (left === null && right === null) return byName(a, b);
    if (left === null) return 1;
    if (right === null) return -1;
    const compared =
      typeof left === 'number' && typeof right === 'number'
        ? left - right
        : String(left).localeCompare(String(right), undefined, { sensitivity: 'base' });
    return compared !== 0 ? sign * compared : byName(a, b);
  });
}

const byName = (a: BrowsableFile, b: BrowsableFile): number =>
  nameOf(a.name).localeCompare(nameOf(b.name), undefined, { sensitivity: 'base' });

/** The creator label resolver the table hands to `sortFiles` — so "Created by" sorts by the name you can
 *  actually read, not by the raw actor enum behind it. */
export function creatorTitleResolver(
  t: (key: string) => string,
  assistantName?: string,
): (item: BrowsableFile) => string | null {
  return (item) => {
    const creator = creatorOf(item);
    return creator ? creatorLabel(creator, t, assistantName) : null;
  };
}
